import { useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon, ArrowTopRightOnSquareIcon, CloudArrowDownIcon, MagnifyingGlassIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import CxRegisterMap from '../components/CxRegisterMap';
import Modal from '../components/Modal';
import {
  BRAND, Note, PageHeader, Panel, States, Stat, gbp, num, selectClass,
} from '../components/OpsPageKit';
import {
  useCxPins, useCxRecord, useCxRegister, useSyncCxRegisters,
} from '../hooks/useCxRegisters';

// One page per CX register: AINM (accidents, incidents, near misses) and
// customer complaints. Both are recorded in SharePoint and still are — this is
// the reading end, where the whole register can be seen at once, filtered, and
// put on a map.
//
// Every filter applies to the map, the counts and the list together. That is
// the point of the page: "show me open utility strikes in Yorkshire" should
// answer itself in one look rather than in an export.

const ukDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { timeZone: 'Europe/London' }) : '—');
const ukDateTime = (d) => (d ? new Date(d).toLocaleString('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
}).replace(',', '') : '—');
const monthLabel = (m) => {
  const [y, mm] = String(m).split('-').map(Number);
  return new Date(Date.UTC(y, mm - 1, 1)).toLocaleDateString('en-GB', { month: 'short' });
};

// SharePoint's internal column names, made readable for the leftovers panel.
const humanise = (k) => k
  .replace(/_x[0-9a-f]{4}_/gi, ' ')
  .replace(/LookupId$/, '')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/^./, (c) => c.toUpperCase());

// SharePoint hyperlink columns come through as { Url, Description }; some
// records hold a bare URL string. Either way, show a link you can click.
const linkOf = (v) => {
  if (v && typeof v === 'object' && typeof v.Url === 'string' && /^https?:\/\//i.test(v.Url)) {
    const d = typeof v.Description === 'string' ? v.Description.trim() : '';
    return { url: v.Url, label: d && !/^https?:\/\//i.test(d) ? d : null };
  }
  if (typeof v === 'string' && /^https?:\/\/\S+$/i.test(v.trim())) return { url: v.trim(), label: null };
  return null;
};
// Friendlier names for the evidence columns (01/10/2026); anything else that
// holds a link still shows, under its humanised column name.
const LINK_LABELS = {
  EvidenceFolder: 'Evidence folder',
  SupplierEvidence: 'Supplier evidence',
  EvidenceLink_x0028_ExternalSubmi: 'External submission evidence',
};

const FILTERS = [
  ['status', 'Status'], ['category', 'Category'], ['subCategory', 'Sub-category'],
  ['area', 'Area'], ['contract', 'Contract'], ['severity', 'Severity'], ['assignedCx', 'Assigned to'],
];
const FILTER_COLUMN = {
  status: 'status',
  category: 'category',
  subCategory: 'sub_category',
  area: 'area',
  contract: 'contract',
  severity: 'severity',
  assignedCx: 'assigned_cx',
};

function TrendStrip({ trend }) {
  const max = Math.max(1, ...trend.map((t) => t.count));
  return (
    <div className="flex items-end gap-1 px-4 py-3" style={{ height: 96 }}>
      {trend.map((t) => (
        <div key={t.month} className="flex flex-1 flex-col justify-end" title={`${t.month}: ${t.count}`}>
          <span className="mb-1 text-center text-[10px] tabular-nums text-gray-400">{t.count || ''}</span>
          <div
            className="w-full rounded-t-sm"
            style={{ height: `${(t.count / max) * 52}px`, background: t.count ? BRAND : '#e5e7eb', minHeight: 2 }}
          />
          <span className="mt-1 text-center text-[10px] text-gray-400">{monthLabel(t.month)}</span>
        </div>
      ))}
    </div>
  );
}

function Flag({ on, children }) {
  if (!on) return null;
  return (
    <span className="inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
      {children}
    </span>
  );
}

function StatusPill({ status, closed }) {
  const isClosed = closed.includes(status);
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${
      isClosed ? 'bg-gray-100 text-gray-600' : 'bg-brand-light text-brand-dark'}`}
    >
      {status || 'No status'}
    </span>
  );
}

function Field({ label, children }) {
  if (children === null || children === undefined || children === '' || children === '—') return null;
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-gray-800">{children}</dd>
    </div>
  );
}

// The full breakdown of one record, which is what the register is for: open a
// pin and see everything CX recorded, including the columns nobody thought to
// ask for when this page was built.
function RecordPanel({ register, id, closed, onClose }) {
  const { data, isLoading, isError } = useCxRecord(register, id);

  return (
    <Modal
      title={data ? (data.reference || `Item ${data.sp_item_id}`) : 'Loading…'}
      subtitle={data ? (data.category || data.event_type || data.title || '') : ''}
      onClose={onClose}
      footer={data ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <a
            href={data.sharePointUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand-dark"
          >
            Open in SharePoint
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
          <p className="text-xs text-gray-400">
            {`Changes are made in SharePoint. Last pulled ${ukDateTime(data.synced_at)}.`}
          </p>
        </div>
      ) : null}
    >
      <div>
        <States isLoading={isLoading} isError={isError} what="this record" />
        {data ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status={data.status} closed={closed} />
              <Flag on={data.hs_related}>H&S</Flag>
              <Flag on={data.safeguarding_related}>Safeguarding</Flag>
              <Flag on={data.environmental_related}>Environmental</Flag>
              <Flag on={data.data_protection_related}>Data protection</Flag>
              <Flag on={data.must_be_recharged}>Recharge</Flag>
              <Flag on={data.potential_nonconformance}>Potential non-conformance</Flag>
              {data.hse_reportable === 'Yes' ? (
                <span className="inline-block rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                  Reportable to HSE
                </span>
              ) : null}
            </div>

            {(() => {
              const links = Object.entries(data.extra || {})
                .map(([k, v]) => ({ key: k, link: linkOf(v) }))
                .filter((x) => x.link);
              if (!links.length) return null;
              return (
                <div className="mt-4">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Evidence</p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {links.map(({ key, link }) => (
                      <a
                        key={key}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-md border border-brand/40 bg-brand-light/40 px-3 py-1.5 text-sm font-medium text-brand hover:bg-brand-light"
                      >
                        {LINK_LABELS[key] || link.label || humanise(key)}
                        <span aria-hidden="true">↗</span>
                      </a>
                    ))}
                  </div>
                </div>
              );
            })()}

            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Reported">{ukDateTime(data.reported_at)}</Field>
              <Field label="Happened">{ukDateTime(data.event_at)}</Field>
              <Field label="Closed">{ukDate(data.closed_at)}</Field>
              <Field label="Callback due">{ukDate(data.callback_at)}</Field>
              <Field label="Assigned to">{data.assigned_cx}</Field>
              <Field label="Reported by">{data.reported_by}</Field>
              <Field label="Employee">{data.employee}</Field>
              <Field label="Contract">{data.contract}</Field>
              <Field label="Area">{data.area}</Field>
              <Field label="Type">{data.record_type}</Field>
              <Field label="Event">{data.event_type}</Field>
              <Field label="Category">{data.category}</Field>
              <Field label="Sub-category">{data.sub_category}</Field>
              <Field label="Severity">{data.severity}</Field>
              <Field label="Received via">{data.source}</Field>
              <Field label="Responsibility">{data.responsibility}</Field>
              <Field label="Cause">{data.cause}</Field>
              <Field label="Human factors">{data.human_factors}</Field>
              <Field label="Preventable">{data.preventable === null ? null : (data.preventable ? 'Yes' : 'No')}</Field>
              <Field label="Anyone injured">{data.injured}</Field>
              <Field label="Hospital treatment">{data.hospital_treatment}</Field>
              <Field label="RIDDOR">{data.riddor}</Field>
            </dl>

            <div className="mt-4">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Address</p>
              <p className="mt-0.5 text-sm text-gray-800">{data.address || '—'}</p>
              <p className="mt-0.5 text-xs text-gray-400">
                {data.latitude
                  ? `Pinned from postcode ${data.postcode}`
                  : (data.postcode ? `Postcode ${data.postcode} is not in the postcode table — not pinned` : 'No postcode in the address — not pinned')}
              </p>
            </div>

            {data.description ? (
              <div className="mt-4">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">What happened</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{data.description}</p>
              </div>
            ) : null}

            {data.cx_comments ? (
              <div className="mt-4">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">CX comments</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{data.cx_comments}</p>
              </div>
            ) : null}

            {(Number(data.total_cost) || Number(data.estimated_cost) || Number(data.confirmed_cost)
              || Number(data.recharge_amount) || Number(data.supplier_cost)) ? (
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border border-gray-200 p-3">
                  <Field label="Estimated">{data.estimated_cost ? gbp(data.estimated_cost) : null}</Field>
                  <Field label="Confirmed">{data.confirmed_cost ? gbp(data.confirmed_cost) : null}</Field>
                  <Field label="Suppliers">{data.supplier_cost ? gbp(data.supplier_cost) : null}</Field>
                  <Field label="Additional">{data.additional_cost ? gbp(data.additional_cost) : null}</Field>
                  <Field label="Recharge">{data.recharge_amount ? gbp(data.recharge_amount) : null}</Field>
                  <Field label="Total to put right">{data.total_cost ? gbp(data.total_cost) : null}</Field>
                </dl>
              ) : null}

            {data.suppliers && data.suppliers.length ? (
              <div className="mt-4">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Suppliers</p>
                <table className="mt-1 w-full text-sm">
                  <tbody>
                    {data.suppliers.map((s) => (
                      <tr key={s.slot} className="border-b border-gray-100 last:border-0 align-top">
                        <td className="py-1.5 pr-2 text-gray-800">
                          {s.supplier || `Supplier ${s.slot}`}
                          {s.work_description ? <span className="block text-xs text-gray-500">{s.work_description}</span> : null}
                        </td>
                        <td className="w-20 py-1.5 text-right tabular-nums text-gray-700">{s.cost ? gbp(s.cost) : '—'}</td>
                        <td className="w-24 py-1.5 pl-2 text-right text-xs text-gray-500">
                          {s.invoice_received ? `Invoiced ${s.invoice_date ? ukDate(s.invoice_date) : ''}` : 'No invoice'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {data.extra && Object.keys(data.extra).length ? (
              <details className="mt-4 rounded-lg border border-gray-200 p-3">
                <summary className="cursor-pointer text-xs font-semibold text-gray-600">
                  {`Everything else on the SharePoint item (${Object.keys(data.extra).length})`}
                </summary>
                <dl className="mt-2 grid grid-cols-1 gap-y-2">
                  {Object.entries(data.extra).map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{humanise(k)}</dt>
                      <dd className="break-words text-sm text-gray-700">
                        {linkOf(v)
                          ? <a href={linkOf(v).url} target="_blank" rel="noopener noreferrer" className="text-brand underline hover:no-underline">{linkOf(v).label || 'Open in SharePoint'}</a>
                          : (typeof v === 'object' ? JSON.stringify(v) : String(v))}
                      </dd>
                    </div>
                  ))}
                </dl>
              </details>
            ) : null}

          </>
        ) : null}
      </div>
    </Modal>
  );
}

function downloadCsv(register, rows) {
  const head = ['Reference', 'Status', 'Category', 'Sub-category', 'Area', 'Contract', 'Severity',
    'Assigned to', 'Employee', 'Reported', 'Happened', 'Closed', 'Address', 'Postcode',
    'Total cost', 'Recharge', 'Pinned'];
  const body = rows.map((r) => [
    r.reference, r.status, r.category, r.sub_category, r.area, r.contract, r.severity,
    r.assigned_cx, r.employee, ukDate(r.reported_at), ukDate(r.event_at), ukDate(r.closed_at),
    r.address, r.postcode, r.total_cost || '', r.recharge_amount || '', r.latitude ? 'Yes' : 'No',
  ]);
  const csv = [head, ...body]
    .map((line) => line.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
  const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${register}-register-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function CxRegister({ register }) {
  const {
    data, isLoading, isError, isFetching, refetch,
  } = useCxRegister(register);
  const pins = useCxPins(register);
  const sync = useSyncCxRegisters();

  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const [openOnly, setOpenOnly] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const closed = (data && data.closedStatuses) || [];

  const rows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.records.filter((r) => {
      if (openOnly && closed.includes(r.status)) return false;
      const filterMiss = Object.entries(filters).some(([key, value]) => (
        value && String(r[FILTER_COLUMN[key]] || '') !== value));
      if (filterMiss) return false;
      if (!q) return true;
      return [r.reference, r.title, r.address, r.postcode, r.category, r.sub_category,
        r.assigned_cx, r.employee, r.event_type, r.area]
        .some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [data, filters, search, openOnly, closed]);

  // Only the filtered rows that actually have a pin reach the map.
  const shownPins = useMemo(() => {
    const keep = new Set(rows.map((r) => r.id));
    const all = (pins.data && pins.data.features) || [];
    return all
      .filter((f) => keep.has(f.id))
      .map((f) => ({ ...f.properties, id: f.id, longitude: f.geometry.coordinates[0], latitude: f.geometry.coordinates[1] }));
  }, [rows, pins.data]);

  const filtered = data && rows.length !== data.records.length;
  const meta = (data && data.register) || {};

  return (
    <div className="p-4 sm:p-6">
      <PageHeader
        title={meta.label || 'Register'}
        subtitle={data
          ? `${meta.full} · kept in SharePoint, pulled hourly · last pulled ${ukDateTime(data.lastSynced)}`
          : 'Loading…'}
        onRefresh={refetch}
        fetching={isFetching}
      >
        <button
          type="button"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-600 hover:border-brand disabled:opacity-50"
        >
          <CloudArrowDownIcon className={`h-4 w-4 ${sync.isPending ? 'animate-pulse' : ''}`} />
          {sync.isPending ? 'Pulling…' : 'Pull from SharePoint'}
        </button>
        <button
          type="button"
          onClick={() => downloadCsv(register, rows)}
          disabled={!rows.length}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-600 hover:border-brand disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          CSV
        </button>
      </PageHeader>

      <States isLoading={isLoading} isError={isError} what="the register" />

      {data ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Records" value={num(data.summary.total)} sub={filtered ? `${num(rows.length)} shown` : 'all shown'} accent />
            <Stat label="Open" value={num(data.summary.open)} tone={BRAND} />
            <Stat label="Closed" value={num(data.summary.closed)} />
            <Stat label="On the map" value={num(data.summary.pinned)} sub={data.summary.unpinned ? `${num(data.summary.unpinned)} without a postcode` : 'all pinned'} />
            <Stat label="Cost to put right" value={gbp(data.summary.totalCost)} />
            <Stat label="Recharged" value={gbp(data.summary.rechargeable)} />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Panel
                title={`Where they happened — ${num(shownPins.length)} pinned`}
                right={<span className="text-xs text-gray-400">Click a pin for the record</span>}
              >
                <CxRegisterMap
                  pins={shownPins}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  closedStatuses={closed}
                />
              </Panel>
            </div>
            <Panel title="Last 12 months">
              <TrendStrip trend={data.trend} />
              <div className="border-t border-gray-100 px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Top categories</p>
                <ul className="mt-1.5 space-y-1">
                  {data.facets.category.slice(0, 6).map((c) => (
                    <li key={c.value} className="flex items-baseline justify-between gap-2 text-sm">
                      <button
                        type="button"
                        onClick={() => setFilters((f) => ({ ...f, category: f.category === c.value ? '' : c.value }))}
                        className={`truncate text-left hover:text-brand ${filters.category === c.value ? 'font-semibold text-brand' : 'text-gray-700'}`}
                      >
                        {c.value}
                      </button>
                      <span className="shrink-0 tabular-nums text-gray-400">{num(c.count)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Panel>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Reference, address, postcode, name…"
                className={`${selectClass} w-64 pl-8`}
              />
            </div>
            {FILTERS.filter(([key]) => (data.facets[key] || []).length > 1).map(([key, label]) => (
              <select
                key={key}
                value={filters[key] || ''}
                onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
                className={selectClass}
              >
                <option value="">{`${label}: all`}</option>
                {data.facets[key].map((o) => (
                  <option key={o.value} value={o.value}>{`${o.value} (${o.count})`}</option>
                ))}
              </select>
            ))}
            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} className="rounded border-gray-300 text-brand focus:ring-brand" />
              Open only
            </label>
            {(Object.values(filters).some(Boolean) || search || openOnly) ? (
              <button
                type="button"
                onClick={() => { setFilters({}); setSearch(''); setOpenOnly(false); }}
                className="text-sm text-brand hover:text-brand-dark"
              >
                Clear
              </button>
            ) : null}
          </div>

          <div className="mt-3">
            <Panel title={`${num(rows.length)} record${rows.length === 1 ? '' : 's'}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2 font-medium">Reference</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">Category</th>
                      <th className="px-4 py-2 font-medium">Address</th>
                      <th className="px-4 py-2 font-medium">Area</th>
                      <th className="px-4 py-2 font-medium">Assigned</th>
                      <th className="px-4 py-2 text-right font-medium">Reported</th>
                      <th className="px-4 py-2 text-right font-medium">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => setSelectedId(r.id)}
                        className={`cursor-pointer border-t border-gray-100 hover:bg-brand-light/40 ${selectedId === r.id ? 'bg-brand-light/60' : ''}`}
                      >
                        <td className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                          <span className="flex items-center gap-1.5">
                            {r.latitude ? <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-brand" /> : <span className="h-3.5 w-3.5 shrink-0" />}
                            {r.reference || `#${r.sp_item_id}`}
                          </span>
                        </td>
                        <td className="px-4 py-2"><StatusPill status={r.status} closed={closed} /></td>
                        <td className="max-w-[16rem] truncate px-4 py-2 text-gray-700" title={r.sub_category || r.category}>
                          {r.sub_category || r.category || r.event_type || '—'}
                        </td>
                        <td className="max-w-[20rem] truncate px-4 py-2 text-gray-600" title={r.address}>{r.address || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-gray-600">{r.area || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-gray-600">{r.assigned_cx || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-gray-600">{ukDate(r.reported_at)}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-gray-600">
                          {Number(r.total_cost) ? gbp(r.total_cost) : '—'}
                        </td>
                      </tr>
                    ))}
                    {!rows.length ? (
                      <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-400">Nothing matches those filters.</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>

          {data.summary.unpinned ? (
            <Note>
              {`${num(data.summary.unpinned)} of ${num(data.summary.total)} records are not on the map. A record is pinned from the postcode in its address field, so one written without a postcode — or with a postcode that is not a real one — has nowhere to go. Fixing the address in SharePoint puts it on the map at the next hourly pull.`}
            </Note>
          ) : null}
        </>
      ) : null}

      {selectedId ? (
        <RecordPanel register={register} id={selectedId} closed={closed} onClose={() => setSelectedId(null)} />
      ) : null}
    </div>
  );
}
